import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Building2, BarChart3, Target, Users, Cpu, 
  TrendingUp, CheckCircle, FileText, PieChart, Layers, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getToolsByLab } from "@/data/aiRegistry";
import BackNavigation from "@/components/BackNavigation";

/**
 * Organization Lab Page
 * Organizational-level workforce intelligence systems
 * Diagnostics, alignment analysis, AI integration strategy
 */
export default function OrganizationLab() {
  const services = [
    {
      icon: BarChart3,
      title: "Workforce Diagnostics",
      description: "Comprehensive assessment of organizational workforce intelligence across all five WAIT dimensions.",
      features: ["Team assessments", "Department analysis", "Organization-wide benchmarking"]
    },
    {
      icon: Target,
      title: "Alignment Analysis",
      description: "Measure and optimize the alignment between workforce capabilities and strategic objectives.",
      features: ["Strategy-capability mapping", "Gap identification", "Alignment recommendations"]
    },
    {
      icon: Cpu,
      title: "AI Integration Strategy",
      description: "Develop and implement AI integration roadmaps tailored to your organization's needs.",
      features: ["AI readiness assessment", "Integration planning", "Change management support"]
    },
    {
      icon: Users,
      title: "Team Development Programs",
      description: "Build workforce intelligence across teams with coordinated development initiatives.",
      features: ["Team diagnostics", "Cohort programs", "Progress tracking"]
    },
    {
      icon: TrendingUp,
      title: "Performance Optimization",
      description: "Apply WAIT Theory insights to improve workforce performance and productivity.",
      features: ["Performance analytics", "Intervention design", "Outcome measurement"]
    },
    {
      icon: PieChart,
      title: "Organizational Benchmarking",
      description: "Compare your organization's workforce intelligence against industry standards.",
      features: ["Industry benchmarks", "Peer comparison", "Trend analysis"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="organization-lab-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Link to="/zodiac-lab" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
                Zodiac Lab
              </Link>
              <span className="text-[#1E3A8A]/40">/</span>
              <span className="text-sm text-[#1E3A8A] font-medium">Organization Lab</span>
            </div>

            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Organizational Intelligence
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
              Organization Lab
            </h1>
            <p className="text-xl font-semibold text-[#1E3A8A] mb-4">
              Build Organizational Workforce Intelligence
            </p>
            <p className="text-base text-[#1E3A8A]/80 leading-relaxed mb-8 max-w-3xl">
              Organization Lab applies WAIT Theory at the company level. Diagnose workforce intelligence 
              across your teams, optimize alignment with strategic goals, and develop AI integration 
              strategies that drive organizational performance.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#2563EB] text-white hover:bg-[#1D4ED8]">
                  Request Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/observatories/global">
                <Button size="lg" variant="outline" className="border-[#991B1B] text-[#991B1B] hover:bg-[#991B1B] hover:text-white">
                  View Benchmarks
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Organization Lab Services
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Comprehensive solutions for building organizational workforce intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
                      <service.icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]/80 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#1E3A8A]/70">
                          <CheckCircle className="w-4 h-4 text-[#2563EB]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Engagement Process
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              A structured approach to building organizational workforce intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Discovery", desc: "Understand your organization's context and goals" },
              { step: 2, title: "Diagnosis", desc: "Assess workforce intelligence across all dimensions" },
              { step: 3, title: "Strategy", desc: "Develop customized development and integration plans" },
              { step: 4, title: "Implementation", desc: "Execute programs with ongoing support and measurement" }
            ].map((phase, index) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {phase.step}
                </div>
                <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                  {phase.title}
                </h3>
                <p className="text-sm text-[#1E3A8A]/70">{phase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lab AI Tool Library Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#991B1B' }}>
                Lab AI Tool Library
              </h2>
              <p className="text-lg text-[#1E3A8A]">
                AI tools curated for organizational workforce intelligence.
              </p>
            </div>
            <Link to="/observatories/ai/registry?lab=organization">
              <Button variant="outline" className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white">
                View All {getToolsByLab('organization').length} Tools
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getToolsByLab('organization').slice(0, 4).map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-md hover:border-[#2563EB]/30 transition-all">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#991B1B] truncate">{tool.name}</h4>
                        <p className="text-xs text-[#1E3A8A]/60">{tool.provider}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#1E3A8A]/70 mb-3 line-clamp-2">
                      {tool.labRelevance.organization.useCases[0]}
                    </p>
                    <a 
                      href={tool.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#2563EB] hover:underline"
                    >
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2563EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Transform Your Organization?
          </h2>
          <p className="text-white/80 mb-8">
            Contact us to discuss your organization's workforce intelligence needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#2563EB] hover:bg-white/90 h-14 px-8">
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
